import { Injectable, computed, signal, WritableSignal } from '@angular/core';

type Theme = 'normal' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService
{
  private readonly storageKey = 'cb-theme';
  private readonly kendoThemeLinkId = 'kendo-theme';

  private readonly _theme: WritableSignal<Theme> = signal(
    ((localStorage.getItem(this.storageKey) as Theme | 'light' | null) ?? 'normal') === 'light' ? 'normal' : (localStorage.getItem(this.storageKey) as Theme) ?? 'normal'
  );
  private readonly _kendoThemeLoadSeq = signal(0);

  public readonly isDarkEffective = computed(() => this._theme() === 'dark');
  public readonly kendoThemeLoadSeq = computed(() => this._kendoThemeLoadSeq());

  constructor()
  {
    this.applyTheme(this._theme());
  }

  get current(): Theme { return this._theme(); }
  set(theme: Theme)
  {
    if (theme === this._theme()) return;
    localStorage.setItem(this.storageKey, theme);
    this._theme.set(theme);
    this.applyTheme(theme);
  }
  setFromSwitch(isDark: boolean) { this.set(isDark ? 'dark' : 'normal'); }
  toggle() { this.set(this.isDarkEffective() ? 'normal' : 'dark'); }

  private applyTheme(theme: Theme)
  {
    const dark = theme === 'dark';
    const value = dark ? 'dark' : 'light';

    const html = document.documentElement;
    const body = document.body;

    // Make variables available to both the app and global overlays
    html.setAttribute('data-theme', value);
    body.setAttribute('data-theme', value);

    html.style.colorScheme = value;
    body.style.colorScheme = value;

    this.applyKendoTheme(dark);
  }

  private async notifyThemeApplied()
  {
    try { await (document as any).fonts?.ready; } catch { }
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    try { window.dispatchEvent(new Event('resize')); } catch { }
    this._kendoThemeLoadSeq.update(v => v + 1);
  }

  private applyKendoTheme(dark: boolean)
  {
    const link = document.getElementById(this.kendoThemeLinkId) as HTMLLinkElement | null;
    if (!link) return;

    const base = 'assets/kendo/';
    const prefer = dark ? 'material-2-dark.css' : 'material-2.css';
    const fallback = dark ? 'material-main-dark.css' : 'material-main.css';

    const setHrefWithHandlers = (href: string, onFallbackHref?: string) =>
    {
      link.onload = null;
      link.onerror = null;

      link.onload = () =>
      {
        link.onload = null;
        link.onerror = null;
        this.notifyThemeApplied();
      };
      if (onFallbackHref)
      {
        link.onerror = () =>
        {
          link.onload = null;
          link.onerror = null;
          // try fallback once
          setHrefWithHandlers(onFallbackHref);
        };
      }
      link.href = href;
    };

    // IMPORTANT: pass fully-qualified URLs for both prefer and fallback
    setHrefWithHandlers(base + prefer, base + fallback);
  }
}
